if (!customElements.get('bought-together')) {
  customElements.define('bought-together', class BoughtTogether extends HTMLElement {
    constructor() {
      super();
      this.isSubmitting = false;
      this.initialized = false;
      this.onDocumentReady = () => this.init();
    }

    connectedCallback() {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', this.onDocumentReady, { once: true });
      } else {
        this.init();
      }
    }

    disconnectedCallback() {
      document.removeEventListener('DOMContentLoaded', this.onDocumentReady);
    }

    init() {
      if (this.initialized || !this.isConnected) return;
      this.discountType = this.dataset.discountType;
      this.discountValue = parseFloat(this.dataset.discountValue) || 0;
      this.comboDiscountValue = parseFloat(this.dataset.comboDiscountValue) || 0;

      this.cards = Array.from(this.querySelectorAll('.js-bt-product-card'));
      this.checkboxes = Array.from(this.querySelectorAll('.js-bt-checkbox'));
      // Bundle products are mandatory; accessories are optional, including default-checked items.
      this.mainCheckboxes = this.checkboxes.filter(checkbox => checkbox.dataset.productType === 'product');
      this.accessoryCheckboxes = this.checkboxes.filter(checkbox => checkbox.dataset.productType === 'accessory');
      this.accessoryToggle = this.querySelector('.js-bt-accessory-toggle');
      this.hiddenAccessoryItems = Array.from(this.querySelectorAll('[data-bt-accessory-item]')).slice(1);
      this.accessoryToggleLabel = this.accessoryToggle?.querySelector('.js-bt-accessory-toggle-label');
      this.accessoryToggleMoreIcon = this.accessoryToggle?.querySelector('.js-bt-accessory-toggle-icon-more');
      this.accessoryToggleLessIcon = this.accessoryToggle?.querySelector('.js-bt-accessory-toggle-icon-less');
      this.discountProgress = Array.from(this.querySelectorAll('.js-bt-discount-progress'))
        .map(element => {
          const steps = Array.from(element.querySelectorAll('.js-bt-progress-step'))
            .map(step => ({
              element: step,
              threshold: Number(step.dataset.threshold) || 0,
              position: Number(step.dataset.positionPercent) || 0
            }))
            .sort((first, second) => first.threshold - second.threshold);

          return {
            track: element.querySelector('.js-bt-progress-track'),
            fill: element.querySelector('.js-bt-progress-fill'),
            selectedCount: element.querySelector('.js-bt-selected-count'),
            steps,
            finalThreshold: steps[steps.length - 1]?.threshold || 0
          };
        })
        .filter(progress => progress.track && progress.fill);
      this.totalPriceElements = Array.from(this.querySelectorAll('.js-bt-total-price'));
      this.originalPriceElement = this.querySelector('.js-bt-original-price');
      this.saveBadge = this.querySelector('.js-bt-save-badge');
      this.saveAmountElement = this.saveBadge?.querySelector('.js-bt-save-amount');
      this.savingsRow = this.querySelector('.js-bt-savings-row');
      this.addButton = this.querySelector('.js-bt-add-btn');
      if (!this.addButton) return;
      this.addButtonLabel = this.addButton.querySelector('span') || this.addButton;
      this.errorElement = this.querySelector('.js-bt-error');
      this.buttonLabel = this.addButton.dataset.label || this.addButton.textContent.trim() || 'Add selected to cart';
      this.moneySample = this.dataset.moneySample || '';
      this.selectedCountOneText = this.dataset.selectedCountOne || '[count] item selected';
      this.selectedCountManyText = this.dataset.selectedCountMany || '[count] items selected';
      this.initialized = true;
      this.cards.forEach(card => {
        const variantData = card.querySelector('.js-bt-variant-data');
        card.checkbox = card.querySelector('.js-bt-checkbox');
        card.optionGroups = Array.from(card.querySelectorAll('[data-option-position]'))
          .sort((first, second) => Number(first.dataset.optionPosition) - Number(second.dataset.optionPosition));
        card.optionLabels = Array.from(card.querySelectorAll('.js-bt-option-value'));
        card.priceElement = card.querySelector('sale-price');
        card.compareElement = card.querySelector('compare-at-price');
        card.image = card.querySelector('.js-bt-product-image img');

        try {
          card.variants = JSON.parse(variantData?.textContent || '[]');
          if (!Array.isArray(card.variants)) card.variants = [];
        } catch (error) {
          card.variants = [];
          console.error('Unable to read bought-together variant data', error);
        }

        card.querySelectorAll('input[type="radio"]').forEach(input => {
          input.addEventListener('change', () => this.updateCardVariant(card, true, input));
        });

        card.querySelector('.js-bt-image-trigger')?.addEventListener('click', event => {
          const target = event.target;

          if (
            !(target instanceof Element) ||
            target.closest('a, button, input, label, select, textarea, [data-option-value]')
          ) {
            return;
          }

          const checkbox = card.checkbox;

          if (!checkbox || checkbox.disabled) {
            return;
          }

          checkbox.checked = !checkbox.checked;
          checkbox.dispatchEvent(new Event('change', { bubbles: true }));
        });

        this.updateCardVariant(card, false);
      });

      this.checkboxes.forEach(checkbox => {
        checkbox.addEventListener('change', () => {
          this.syncCardSelection(checkbox);
          this.updateTotal();
        });
      });

      this.addButton.addEventListener('click', () => this.addToCart());
      this.accessoryToggle?.addEventListener('click', () => this.toggleAccessories());
      this.updateTotal();
    }

    toggleAccessories() {
      if (!this.accessoryToggle) return;

      const expanded = this.accessoryToggle.getAttribute('aria-expanded') !== 'true';
      const toggleText = expanded
        ? this.accessoryToggle.dataset.viewLess
        : this.accessoryToggle.dataset.viewMore;

      this.accessoryToggle.setAttribute('aria-expanded', String(expanded));
      this.accessoryToggle.setAttribute('aria-label', toggleText);
      if (this.accessoryToggleLabel) this.accessoryToggleLabel.textContent = toggleText;
      this.accessoryToggleMoreIcon?.classList.toggle('hidden', expanded);
      this.accessoryToggleLessIcon?.classList.toggle('hidden', !expanded);

      this.hiddenAccessoryItems.forEach(item => {
        item.classList.toggle('hidden', !expanded);
      });
    }

    syncCardSelection(checkbox) {
      const card = checkbox.closest('.js-bt-product-card');

      if (card) {
        card.dataset.selected = checkbox.checked ? 'true' : 'false';
      }
    }

    getMatchingVariant(card) {
      if (!card.variants?.length) {
        return null;
      }

      const optionGroups = card.optionGroups;

      if (optionGroups.length === 0) {
        const currentVariantId = Number(card.checkbox?.dataset.variantId);
        return card.variants.find(variant => variant.id === currentVariantId) || card.variants[0];
      }

      const selectedOptions = optionGroups.map(group => group.querySelector('input[type="radio"]:checked')?.value);
      return card.variants.find(variant => {
        return variant.options?.every((value, index) => value === selectedOptions[index]);
      });
    }

    formatPrice(cents) {
      const formattedFromSample = this.formatPriceFromSample(cents);

      if (formattedFromSample) {
        return formattedFromSample;
      }

      const moneySettings = window.themeVariables?.settings || {};
      const moneyFormat = moneySettings.currencyCodeEnabled
        ? moneySettings.moneyWithCurrencyFormat
        : moneySettings.moneyFormat;
      const formatter = window.Shopify?.formatMoney || window.formatMoney;

      if (typeof formatter === 'function') {
        return formatter(cents, moneyFormat || '{{amount}}');
      }

      return (Number(cents || 0) / 100).toFixed(2);
    }

    formatPriceFromSample(cents) {
      const sample = this.moneySample.trim();
      const numericMatch = sample.match(/\d[\d.,\s\u00a0'’]*\d|\d/);

      if (!sample || !numericMatch) {
        return '';
      }

      const amountToken = numericMatch[0];
      const lastDot = amountToken.lastIndexOf('.');
      const lastComma = amountToken.lastIndexOf(',');
      const decimalSeparatorIndex = Math.max(lastDot, lastComma);
      const digitsAfterSeparator = decimalSeparatorIndex >= 0
        ? amountToken.length - decimalSeparatorIndex - 1
        : 0;
      const hasDecimal = decimalSeparatorIndex >= 0 && digitsAfterSeparator > 0 && digitsAfterSeparator < 3;
      const decimalSeparator = hasDecimal ? amountToken[decimalSeparatorIndex] : '';
      const decimalDigits = hasDecimal ? digitsAfterSeparator : 0;
      const integerToken = hasDecimal ? amountToken.slice(0, decimalSeparatorIndex) : amountToken;
      const groupSeparatorMatch = integerToken.match(/[.,\s\u00a0'’]/);
      const groupSeparator = groupSeparatorMatch ? groupSeparatorMatch[0] : '';
      const prefix = sample.slice(0, numericMatch.index);
      const suffix = sample.slice(numericMatch.index + amountToken.length);
      const numericValue = hasDecimal
        ? (Math.round(Number(cents) || 0) / 100).toFixed(decimalDigits)
        : Math.round((Number(cents) || 0) / 100).toFixed(0);
      const parts = numericValue.split('.');
      const integerValue = parts[0];
      const decimalValue = parts[1] || '';
      const groupedInteger = groupSeparator
        ? integerValue.replace(/\B(?=(\d{3})+(?!\d))/g, groupSeparator)
        : integerValue;
      const formattedAmount = hasDecimal
        ? `${groupedInteger}${decimalSeparator}${decimalValue}`
        : groupedInteger;

      return `${prefix}${formattedAmount}${suffix}`;
    }

    getActiveDiscountValue() {
      if (this.discountType !== 'percentage') {
        return 0;
      }

      const allMainProductsSelected = this.mainCheckboxes.length > 0
        && this.mainCheckboxes.every(checkbox => checkbox.checked);
      if (!allMainProductsSelected) {
        return 0;
      }

      const hasSelectedAccessory = this.accessoryCheckboxes.some(checkbox => checkbox.checked);
      const configuredDiscount = hasSelectedAccessory && this.comboDiscountValue > 0
        ? this.comboDiscountValue
        : this.discountValue;

      return Math.min(Math.max(configuredDiscount, 0), 100);
    }

    formatSelectedCount(count) {
      const template = count === 1 ? this.selectedCountOneText : this.selectedCountManyText;
      return template.replace(/\[count\]/g, String(count));
    }

    updateDiscountProgress(selectedCount) {
      this.discountProgress.forEach(progress => {
        const { track, fill, selectedCount: selectedCountElement, steps, finalThreshold } = progress;
        const progressValue = Math.min(Math.max(selectedCount, 0), finalThreshold);
        let progressPercent = finalThreshold > 0 ? (progressValue / finalThreshold) * 100 : 0;

        if (steps.length > 1) {
          const firstThreshold = steps[0].threshold;
          const firstPosition = steps[0].position;
          progressPercent = selectedCount >= finalThreshold
            ? 100
            : selectedCount >= firstThreshold
              ? firstPosition + ((selectedCount - firstThreshold) / (finalThreshold - firstThreshold)) * (100 - firstPosition)
              : firstThreshold > 0 ? (selectedCount / firstThreshold) * firstPosition : 0;
        }

        fill.style.width = `${progressPercent}%`;
        track.setAttribute('aria-valuenow', String(progressValue));
        steps.forEach(step => {
          step.element.dataset.reached = String(selectedCount >= step.threshold);
        });
        if (selectedCountElement) {
          selectedCountElement.textContent = this.formatSelectedCount(selectedCount);
        }
      });
    }

    getDiscountedPrice(price, discountValue) {
      const basePrice = Number(price) || 0;

      if (discountValue <= 0) {
        return basePrice;
      }

      return Math.round(basePrice * (1 - discountValue / 100));
    }

    updateCardPrice(card, price, compareAtPrice, discountValue) {
      const priceElement = card.priceElement;
      const compareElement = card.compareElement;
      const displayPrice = this.getDiscountedPrice(price, discountValue);
      const originalPrice = Math.max(Number(compareAtPrice) || 0, Number(price) || 0);
      const hasCompareAtPrice = originalPrice > displayPrice;

      if (priceElement) {
        this.setMoney(priceElement, this.formatPrice(displayPrice));
      }

      if (compareElement) {
        this.setMoney(compareElement, this.formatPrice(originalPrice));
        compareElement.toggleAttribute('hidden', !hasCompareAtPrice);
      }
    }

    setMoney(element, formattedPrice) {
      if (element.btFormattedPrice === formattedPrice) return;
      const priceLabel = element.querySelector('.sr-only')?.outerHTML || '';
      element.innerHTML = `${priceLabel}${formattedPrice}`;
      element.btFormattedPrice = formattedPrice;
    }

    setVisible(element, visible) {
      if (!element) return;
      element.classList.toggle('hidden', !visible);
      element.hidden = !visible;
    }

    setButtonLabel(label) {
      this.addButtonLabel.textContent = label;
    }

    updateCardPrices(discountValue) {
      this.cards.forEach(card => {
        const checkbox = card.checkbox;

        if (!checkbox) {
          return;
        }

        this.updateCardPrice(
          card,
          Number(checkbox.dataset.price) || 0,
          Number(checkbox.dataset.compareAtPrice) || 0,
          discountValue
        );
      });
    }

    updateCardVariant(card, updateTotals = true, changedInput = null) {
      let variant = this.getMatchingVariant(card);
      if (!variant && changedInput) {
        const position = Number(changedInput.closest('[data-option-position]').dataset.optionPosition) - 1;
        variant = card.variants.find(item => item.options[position] === changedInput.value);
      }
      variant ||= card.variants?.[0];

      if (!variant || !card.checkbox) {
        return;
      }

      card.optionGroups.forEach(group => {
        const position = Number(group.dataset.optionPosition) - 1;
        const input = Array.from(group.querySelectorAll('input[type="radio"]'))
          .find(option => option.value === variant.options[position]);
        if (input) input.checked = true;
      });

      const checkbox = card.checkbox;
      const compareAtPrice = Number(variant.compare_at_price) || 0;

      card.dataset.variantId = variant.id;
      checkbox.dataset.variantId = variant.id;
      checkbox.dataset.price = variant.price;
      checkbox.dataset.compareAtPrice = compareAtPrice;

      this.syncCardSelection(checkbox);

      // Update option value labels
      if (variant.options) {
        variant.options.forEach((value, index) => {
          const label = card.optionLabels.find(item => Number(item.dataset.optionIndex) === index);
          if (label) {
            label.textContent = value;
          }
        });
      }

      if (variant.image && card.dataset.variantImage !== variant.image) {
        const image = card.image;

        if (image) {
          image.src = variant.image;
          image.removeAttribute('data-src');
          image.removeAttribute('data-srcset');
          image.removeAttribute('srcset');
          image.alt = `${card.dataset.productTitle || ''} - ${variant.options?.join(', ') || ''}`.trim();
          card.dataset.variantImage = variant.image;
        }
      }

      if (updateTotals) {
        this.updateTotal();
      }
    }

    updateTotal() {
      const activeDiscountValue = this.getActiveDiscountValue();
      this.updateCardPrices(activeDiscountValue);

      let finalTotal = 0;
      let compareAtTotal = 0;
      const selectedCheckboxes = this.checkboxes.filter(checkbox => checkbox.checked);

      selectedCheckboxes.forEach(checkbox => {
        const price = Number(checkbox.dataset.price) || 0;
        const compareAtPrice = Number(checkbox.dataset.compareAtPrice) || price;
        finalTotal += this.getDiscountedPrice(price, activeDiscountValue);
        compareAtTotal += Math.max(compareAtPrice, price);
      });

      this.updateDiscountProgress(selectedCheckboxes.length);

      const formattedPrice = this.formatPrice(finalTotal);
      const formattedOriginalPrice = this.formatPrice(compareAtTotal);

      this.totalPriceElements.forEach(el => {
        this.setMoney(el, formattedPrice);
      });

      if (this.originalPriceElement) {
        const hasOriginalPrice = compareAtTotal > finalTotal && compareAtTotal > 0;

        if (hasOriginalPrice) {
          this.setMoney(this.originalPriceElement, formattedOriginalPrice);
        }

        this.setVisible(this.originalPriceElement, hasOriginalPrice);
      }

      const hasSavings = compareAtTotal > finalTotal;
      this.setVisible(this.saveBadge, hasSavings);
      this.setVisible(this.savingsRow, hasSavings);

      if (hasSavings && this.saveAmountElement) {
        // Money formatters (including Transcy) may return HTML wrappers.
        this.setMoney(this.saveAmountElement, this.formatPrice(compareAtTotal - finalTotal));
      }

      if (!this.isSubmitting) {
        this.setButtonLabel(this.buttonLabel);
      }

      this.addButton.disabled = this.isSubmitting || selectedCheckboxes.length === 0;
    }

    async addItems(items) {
      const rootUrl = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${rootUrl}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items,
          sections: ['cart-drawer', 'variant-added'],
          sections_url: window.location.pathname
        })
      });
      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.description || responseData.message || 'Failed to add to cart');
      }

      return responseData;
    }

    async syncCart(addResponse) {
      const rootUrl = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${rootUrl}cart.js`, {
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        throw new Error('Unable to refresh cart');
      }

      const cart = await response.json();
      cart.sections = addResponse.sections || {};

      if (cart.sections['cart-drawer']) {
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'variant:add',
            cart
          }
        }));
      } else {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }

      this.dispatchEvent(new CustomEvent('variant:add', {
        bubbles: true,
        detail: {
          items: addResponse.items || [],
          cart,
          blockCartDrawerOpening: true
        }
      }));

      return cart;
    }

    waitForUpcart(timeout = 3000) {
      if (typeof window.upcartOpenCart === 'function') {
        return Promise.resolve(window.upcartOpenCart);
      }

      return new Promise(resolve => {
        const startedAt = Date.now();
        const intervalId = window.setInterval(() => {
          if (typeof window.upcartOpenCart === 'function') {
            window.clearInterval(intervalId);
            resolve(window.upcartOpenCart);
            return;
          }

          if (Date.now() - startedAt >= timeout) {
            window.clearInterval(intervalId);
            resolve(null);
          }
        }, 100);
      });
    }

    async openUpcartWhenReady() {
      const openCart = await this.waitForUpcart();

      if (!openCart) {
        console.warn('UpCart did not become available after the bundle was added.');
        return false;
      }

      if (typeof window.upcartRefreshCart === 'function') {
        let refreshTimeoutId;

        try {
          await Promise.race([
            Promise.resolve(window.upcartRefreshCart()),
            new Promise((resolve, reject) => {
              refreshTimeoutId = window.setTimeout(() => {
                reject(new Error('UpCart refresh timed out after 3 seconds'));
              }, 3000);
            })
          ]);
        } catch (error) {
          console.warn('Unable to refresh UpCart before opening:', error);
        } finally {
          window.clearTimeout(refreshTimeoutId);
        }
      }

      try {
        openCart.call(window);
        return true;
      } catch (error) {
        console.error('Unable to open UpCart:', error);
        return false;
      }
    }

    openNativeCart(addResponse, cart) {
      const cartType = window.themeVariables?.settings?.cartType;
      const nativeCart = cartType === 'drawer'
        ? document.querySelector('cart-drawer')
        : document.querySelector('cart-notification-drawer');

      if (cart && nativeCart && cartType !== 'page') {
        this.dispatchEvent(new CustomEvent('variant:add', {
          bubbles: true,
          detail: {
            items: addResponse.items || [],
            cart,
            blockCartDrawerOpening: false
          }
        }));
        return;
      }

      const rootUrl = window.Shopify?.routes?.root || '/';
      window.location.assign(`${rootUrl}cart`);
    }

    async addToCart() {
      const items = this.checkboxes
        .filter(checkbox => checkbox.checked)
        .map(checkbox => ({ id: Number(checkbox.dataset.variantId), quantity: 1 }))
        .filter(item => Number.isSafeInteger(item.id) && item.id > 0);
      if (!items.length || this.isSubmitting) return;

      this.isSubmitting = true;
      this.addButton.disabled = true;
      this.addButton.setAttribute('aria-busy', 'true');
      this.setButtonLabel('Adding...');
      if (this.errorElement) {
        this.errorElement.hidden = true;
        this.errorElement.textContent = '';
      }

      try {
        const addResponse = await this.addItems(items);
        this.setButtonLabel('Added');
        let cart = null;

        try {
          cart = await this.syncCart(addResponse);
        } catch (error) {
          console.warn('Bundle was added, but the theme cart could not be refreshed:', error);
        }

        const upcartOpened = await this.openUpcartWhenReady();
        if (!upcartOpened) {
          this.openNativeCart(addResponse, cart);
        }
      } catch (error) {
        console.error('BT Add to Cart Error:', error);
        if (this.errorElement) {
          this.errorElement.textContent = error.message;
          this.errorElement.hidden = false;
        }
        this.dispatchEvent(new CustomEvent('cart:error', {
          bubbles: true,
          detail: { error: error.message }
        }));
      } finally {
        this.isSubmitting = false;
        this.addButton.removeAttribute('aria-busy');
        this.updateTotal();
      }
    }
  });
}
